import { supabase } from './supabase'

// Duplicate an existing order into a fresh draft.
//
// Copies: company, card, delivery address/postcode, client notes, and every
// consignment + line with product/qty/price/pack size snapshots and recipient
// details preserved so a repeat order takes one click.
//
// Clears: reference (auto-generated), status (defaults to draft), PO, requested
// delivery date, placed_at/by, sage push data — the user picks a new PO/date
// and hits submit.
//
// Returns { id } of the new draft on success, or throws.
export async function reorderFromOrder(sourceOrderId) {
  if (!sourceOrderId) throw new Error('No source order id')

  // 1. Load the source order + its consignments + its lines.
  const [{ data: src, error: eOrd }, { data: cons, error: eCon }, { data: lines, error: eLn }] = await Promise.all([
    supabase.from('orders')
      .select('company_id, card_id, delivery_address, delivery_postcode, client_notes')
      .eq('id', sourceOrderId).single(),
    supabase.from('order_consignments')
      .select('id, recipient_name, recipient_ref, sort_order')
      .eq('order_id', sourceOrderId)
      .order('sort_order', { ascending: true }),
    supabase.from('order_lines')
      .select('product_id, line_number, sku_at_order, name_at_order, description_at_order, unit_at_order, price_at_order, qty, pack_size_at_order, recipient_name, recipient_ref, consignment_id')
      .eq('order_id', sourceOrderId)
      .order('line_number', { ascending: true }),
  ])
  if (eOrd) throw eOrd
  if (eCon) throw eCon
  if (eLn)  throw eLn
  if (!src) throw new Error('Source order not found')

  // 2. Create the new draft order.
  const { data: newOrder, error: eNew } = await supabase.from('orders').insert({
    company_id: src.company_id,
    card_id: src.card_id,
    delivery_address: src.delivery_address,
    delivery_postcode: src.delivery_postcode,
    client_notes: src.client_notes,
    status: 'draft',
  }).select('id').single()
  if (eNew) throw eNew

  // 3. Copy consignments and build old-id → new-id map for the lines.
  const conMap = new Map()
  if (cons && cons.length > 0) {
    const rows = cons.map(c => ({
      order_id: newOrder.id,
      recipient_name: c.recipient_name,
      recipient_ref: c.recipient_ref,
      sort_order: c.sort_order,
    }))
    const { data: inserted, error: eIns } = await supabase.from('order_consignments')
      .insert(rows).select('id, sort_order')
    if (eIns) throw eIns
    // Match by sort_order + insert order. Both arrays were sorted by sort_order
    // so index-alignment holds.
    cons.forEach((c, i) => conMap.set(c.id, inserted[i].id))
  }

  // 4. Copy lines, mapping consignment_id through the new ids.
  if (lines && lines.length > 0) {
    const lineRows = lines.map(l => ({
      order_id: newOrder.id,
      product_id: l.product_id,
      line_number: l.line_number,
      sku_at_order: l.sku_at_order,
      name_at_order: l.name_at_order,
      description_at_order: l.description_at_order,
      unit_at_order: l.unit_at_order,
      price_at_order: l.price_at_order,
      qty: l.qty,
      pack_size_at_order: l.pack_size_at_order,
      recipient_name: l.recipient_name,
      recipient_ref: l.recipient_ref,
      consignment_id: l.consignment_id ? conMap.get(l.consignment_id) : null,
    }))
    const { error: eLns } = await supabase.from('order_lines').insert(lineRows)
    if (eLns) throw eLns
  }

  return { id: newOrder.id }
}
